package dev.lingvo.shared.screenshots.color

import kotlin.math.sqrt
import kotlin.random.Random

/** Interleaved RGBA channels keep analysis independent of browser pixel packing and machine endianness. */
object IconPalettes : IconPaletteExtractor {
    /** Removes a uniform icon field before returning clusters in descending population order. */
    override fun extract(rgba: IntArray, width: Int, height: Int): List<String> =
        clusters(rgba, width, height).map { gamutMap(it.lab.toLch()) }

    /** Uses population as well as chroma so a small saturated glyph can beat a large muted field. */
    fun accent(rgba: IntArray, width: Int, height: Int): String? {
        val clusters = clusters(rgba, width, height)
        val total = clusters.sumOf { it.population }.toDouble()
        return clusters.filter { it.lab.toLch().c >= .04 }
            .maxByOrNull { sqrt(it.population / total) * it.lab.toLch().c }
            ?.let { gamutMap(it.lab.toLch()) }
    }

    internal fun sampleGrid(width: Int, height: Int): Pair<Int, Int> {
        require(width > 0 && height > 0)
        val columns = width.coerceAtMost(64)
        val rows = height.coerceAtMost(4096 / columns)
        return ((width.toLong() + columns - 1) / columns).toInt() to ((height.toLong() + rows - 1) / rows).toInt()
    }

    private data class Cluster(val lab: OkLab, val population: Int)

    private fun clusters(rgba: IntArray, width: Int, height: Int): List<Cluster> {
        require(width > 0 && height > 0 && width.toLong() * height * 4 == rgba.size.toLong()) { "Expected width * height interleaved RGBA pixels" }
        val (stepX, stepY) = sampleGrid(width, height)
        val points = mutableListOf<OkLab>()
        val ring = mutableListOf<OkLab>()
        for (y in 0 until height step stepY) for (x in 0 until width step stepX) {
            val offset = (y * width + x) * 4
            require((0..3).all { rgba[offset + it] in 0..255 }) { "RGBA channels must be bytes" }
            if (rgba[offset + 3] < 200) continue
            val hex = "#" + (0..2).joinToString("") { rgba[offset + it].toString(16).uppercase().padStart(2, '0') }
            val lab = srgbHexToOkLab(hex)
            points += lab
            if (x < width * .06 || x >= width * .94 || y < height * .06 || y >= height * .94) ring += lab
        }
        if (points.isEmpty()) return emptyList()
        val mean = ring.takeIf { it.isNotEmpty() }?.let { colors -> OkLab(colors.map { it.l }.average(), colors.map { it.a }.average(), colors.map { it.b }.average()) }
        val background = mean?.takeIf { candidate -> ring.all { distance(it, candidate) <= .0004 } }
        val samples = if (background == null) points else points.filter { distance(it, background) > .0004 }
        if (samples.isEmpty()) return emptyList()
        val random = Random(42)
        val centres = mutableListOf(samples[random.nextInt(samples.size)])
        while (centres.size < 5) {
            val distances = samples.map { sample -> centres.minOf { distance(sample, it) } }
            val total = distances.sum()
            if (total <= 1e-16) break
            var pick = random.nextDouble() * total
            var selected = samples.last()
            for (i in samples.indices) {
                pick -= distances[i]
                if (pick <= 0) { selected = samples[i]; break }
            }
            centres += selected
        }
        fun groups(): List<List<OkLab>> {
            val groups = List(centres.size) { mutableListOf<OkLab>() }
            for (point in samples) groups[centres.indices.minBy { distance(point, centres[it]) }] += point
            return groups
        }
        repeat(12) {
            groups().forEachIndexed { i, group ->
                if (group.isNotEmpty()) centres[i] = OkLab(group.map { it.l }.average(), group.map { it.a }.average(), group.map { it.b }.average())
            }
        }
        return groups().mapIndexedNotNull { i, group -> if (group.isEmpty()) null else Cluster(centres[i], group.size) }
            .sortedByDescending { it.population }
    }

    private fun distance(a: OkLab, b: OkLab): Double =
        (a.l - b.l) * (a.l - b.l) + (a.a - b.a) * (a.a - b.a) + (a.b - b.b) * (a.b - b.b)
}
